import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';

import Grid from '@material-ui/core/Grid';

import { isoDateTimeToLabel, formatAmount, sqlDateToLabel } from 'helpers/helpers';
import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import P from 'apps/Pdf/components/common/P';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import Loading from 'apps/Pdf/components/common/Loading';
import Signature from 'apps/Pdf/components/Signature/Signature';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import { MoneyWithdrawalContent } from './MoneyWithdrawalInterface';
import { AccountStatus, Currency } from 'enums';
import { PdfCategory } from '../Interfaces';

const MoneyWithdrawal = (params: PdfContentParams) => {
  const styles = useGeneralStyles();

  const pdfContent = usePdfContent<MoneyWithdrawalContent>({
    category: PdfCategory.SIGNATURE,
    templateType: TPL_MONEY_WITHDRAWAL,
    ...params,
  });

  const data = pdfContent?.data ? pdfContent.data : ({} as MoneyWithdrawalContent);
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>
        {data.status === AccountStatus.ACTIVE && (
          <>Žádost o výběr peněz (nebo uzavření účtu) k {isoDateTimeToLabel(data.date, 'd. M. yyyy')}</>
        )}
        {data.status === AccountStatus.AWAITING_APPROVAL && <>Uzavření účtu</>}
      </Title>
      <BasicData data={pdfContent.data!.header} />
      {data.status === AccountStatus.ACTIVE && (
        <>
          <P>Žádám o níže uvedenou transakci:</P>
          <H1>Výběr</H1>
          <div className={`${styles.twoColumns}`}>
            <Grid container className={styles.lines}>
              <H2>Částka výběru</H2>
              <Grid item xs={12} className={styles.mediumText}>
                {data.withdrawAll ? 'vybrat vše' : formatAmount(data.amount, currency)}
              </Grid>
            </Grid>
            <Grid container className={styles.lines}>
              <H2>Na bankovní účet</H2>
              <Grid item xs={12} className={styles.regularText}>
                {data.bankAccount.iban || `${data.bankAccount.fullNumber}`}
              </Grid>
            </Grid>
          </div>
          <P>
            Po potvrzení může trvat 7—15 dní, než se provedou všechny transakce a hotovost se převede na účet zákazníka.
          </P>
          {data.cancelAccount && (
            <>
              <H1>Změny v nastavení</H1>
              <P>Po výběru se účet uzavře a nebude možné jej dále používat.</P>
            </>
          )}
        </>
      )}
      {data.status === AccountStatus.AWAITING_APPROVAL && (
        <>
          <P>Žádám tímto o uzavření mého účtu:</P>
          <P>
            název: <strong>{pdfContent.data!.header.accountTitle}</strong>
            <br />
            číslo účtu: <strong>{pdfContent.data!.variableSymbol}</strong>
          </P>
          <P>Účet bude uzavřen po podpisu klienta a nebude možné jej dále používat.</P>
        </>
      )}
      {data.ltipAllowed === false && data.minStart && (
        <P className={styles.bold}>
          Tento účet je v režimu dlouhodobý investiční produkt. Klient může vybírat peníze nejdříve ve věku 60let a 10
          let od založení účtu, nejdříve tedy {sqlDateToLabel(data.minStart, 'dd. MM. yyyy')}. Po dřívějším výběru je
          povinen podat daňové přiznání a také dodanit předchozí období, kdy si uplatňoval daňovou úlevu.
        </P>
      )}

      {data.header.mode === 'LTIP' && data.cancelAccount && (
        <>
          <H1>Prohlášení o předčasném výběru z důchodového účtu</H1>
          <P>
            V souvislosti s podáním Pokynu, který má za následek Předčasný výběr z Důchodového účtu WOOD ve smyslu
            příslušných Produktových podmínek, tímto prohlašuji, že
          </P>
          <ol className={`${styles.regularText} ${styles.list} ${styles.listA}`}>
            <li>
              jsem od WOOD RI obdržel/a dokument „Následky předčasného výběru z Důchodového účtu WOOD“ na trvalém
              nosiči dat;
            </li>
            <li>
              jsem si vědom/a toho, že Předčasný výběr má za následek navrácení Daňové podpory, jejíž mechanismus je
              popsán v dokumentu „Následky předčasného výběru z Důchodového účtu WOOD“; a
            </li>
            <li>
              jsem si vědom/a toho, že můj účet WOOD DIP nebude po provedení Předčasného výběru nadále daňově
              podporován.
            </li>
          </ol>
          <P>
            Tímto potvrzuji, že i přes všechny popsané následky plynoucí z Předčasného výběru trvám na tom, aby WOOD RI
            provedlo Pokyn, který má za následek Předčasný výběr z mého WOOD DIP účtu.
          </P>
        </>
      )}

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_MONEY_WITHDRAWAL: TemplateType = 'moneyWithdrawal';
export { TPL_MONEY_WITHDRAWAL, MoneyWithdrawal };
